/**
 * MAUD ERP Layout Wrapper
 * Wraps the shared Layout component with MAUD ERP specific menu items
 */

import React, { useState, useEffect, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../../../components/Header';
import Sidebar from '../../../components/Sidebar';
import ToastContainer from '../../../components/feature/ToastContainer';
import { useToast } from '../../../hooks/useToast';
import { ThemeService } from '../../../services/themeService';
import { NOTIFICATION_EVENT_NAME, type NotificationEvent } from '../../../services/notificationService';
import { useSidebarStore } from '@/stores/sidebarStore';
import { useAuth } from '../../../context/AuthContext';
import { TableSkeleton } from '@/components/base/Skeleton';

interface SubItem {
  mnu_itm_nm: string;
  t_mnu_itm_nm?: string;
  mnu_dscn_tx?: string;
  mnu_itm_icn_tx?: string;
  mnu_itm_icn_clr_tx?: string;
  mnu_itm_url_tx: string;
  mnu_vw_in?: 'sidemenu' | 'submenu' | 'cardmenu';
  prnt_mnu_itm_id?: number;
  hdr_in?: number;
  children?: SubItem[];
}

interface NavItem {
  mnu_itm_nm: string;
  t_mnu_itm_nm?: string;
  mnu_dscn_tx?: string;
  mnu_itm_icn_tx?: string;
  mnu_itm_icn_clr_tx?: string;
  mnu_itm_url_tx: string;
  mnu_vw_in?: 'sidemenu' | 'submenu' | 'cardmenu';
  prnt_mnu_itm_id?: number;
  hdr_in?: number;
  children?: SubItem[];
}

export const maudErpMenuItems: NavItem[] = [
  {
    mnu_itm_nm: 'Dashboard',
    t_mnu_itm_nm: 'డాష్‌బోర్డ్',
    mnu_itm_url_tx: '/app/maud-erp',
    mnu_itm_icn_tx: 'ri-dashboard-line',
    mnu_dscn_tx: 'MAUD ERP Dashboard'
  },
  {
    mnu_itm_nm: 'Asset Management',
    t_mnu_itm_nm: 'ఆస్తుల నిర్వహణ',
    mnu_itm_url_tx: '/app/maud-erp/asset-management',
    mnu_itm_icn_tx: 'ri-building-2-line',
    mnu_dscn_tx: 'Municipal Asset Management'
  },
  {
    mnu_itm_nm: 'Municipal Council',
    t_mnu_itm_nm: 'మునిసిపల్ కౌన్సిల్',
    mnu_itm_url_tx: '/app/maud-erp/municipal-council',
    mnu_itm_icn_tx: 'ri-government-line',
    mnu_dscn_tx: 'Council Meetings Management'
  },
  {
    mnu_itm_nm: 'Services',
    t_mnu_itm_nm: 'సేవలు',
    mnu_itm_url_tx: '/app/maud-erp/services',
    mnu_itm_icn_tx: 'ri-customer-service-2-line',
    mnu_dscn_tx: 'Citizen Services'
  },
  {
    mnu_itm_nm: 'Leases & Agreements',
    t_mnu_itm_nm: 'లీజులు & ఒప్పందాలు',
    mnu_itm_url_tx: '/app/maud-erp/leases-agreements',
    mnu_itm_icn_tx: 'ri-file-text-line',
    mnu_dscn_tx: 'Leases and Agreements Management'
  },
  {
    mnu_itm_nm: 'Finance',
    t_mnu_itm_nm: 'ఆర్థిక',
    mnu_itm_url_tx: '/app/maud-erp/finance-management',
    mnu_itm_icn_tx: 'ri-money-dollar-circle-line',
    mnu_dscn_tx: 'Financial Management'
  },
  {
    mnu_itm_nm: 'Legal Cases',
    t_mnu_itm_nm: 'చట్టపరమైన కేసులు',
    mnu_itm_url_tx: '/app/maud-erp/legal-case-management',
    mnu_itm_icn_tx: 'ri-scales-3-line',
    mnu_dscn_tx: 'Legal Case Management'
  },
  {
    mnu_itm_nm: 'Disciplinary Cases',
    t_mnu_itm_nm: 'క్రమశిక్షణా కేసులు',
    mnu_itm_url_tx: '/app/maud-erp/disciplinary-cases',
    mnu_itm_icn_tx: 'ri-file-warning-line',
    mnu_dscn_tx: 'Employee Disciplinary Cases Management'
  },
  {
    mnu_itm_nm: 'Display Devices',
    t_mnu_itm_nm: 'ప్రదర్శన పరికరాలు',
    mnu_itm_url_tx: '/app/maud-erp/display-devices',
    mnu_itm_icn_tx: 'ri-tv-2-line',
    mnu_dscn_tx: 'Display Devices Management (GO Ms.No.253)',
    children: [
      { mnu_itm_nm: 'Dashboard', mnu_itm_url_tx: '/app/maud-erp/display-devices', mnu_itm_icn_tx: 'ri-dashboard-line' },
      { mnu_itm_nm: 'Search', mnu_itm_url_tx: '/app/maud-erp/display-devices/search', mnu_itm_icn_tx: 'ri-search-2-line' },
      { mnu_itm_nm: 'Displayers', mnu_itm_url_tx: '/app/maud-erp/display-devices/displayers', mnu_itm_icn_tx: 'ri-user-star-line' },
      { mnu_itm_nm: 'Devices', mnu_itm_url_tx: '/app/maud-erp/display-devices/devices', mnu_itm_icn_tx: 'ri-tv-2-line' },
      { mnu_itm_nm: 'Service Requests', mnu_itm_url_tx: '/app/maud-erp/display-devices/workflow', mnu_itm_icn_tx: 'ri-flow-chart' },
      // My Inbox is where an officer ACTS on a file (approve / forward / reject /
      // sign) — Service Requests above is the read-only monitoring view. This
      // hardcoded list overrides the DB menu for Display Devices, so the DB grant
      // of "My Inbox" (mnu_itm 1000929) never surfaced until it was added here too.
      { mnu_itm_nm: 'Inbox', t_mnu_itm_nm: 'నా ఇన్‌బాక్స్', mnu_itm_url_tx: '/app/maud-erp/display-devices/inbox', mnu_itm_icn_tx: 'ri-inbox-archive-line' },
      { mnu_itm_nm: 'DCB Reports', mnu_itm_url_tx: '/app/maud-erp/display-devices/demand-collection', mnu_itm_icn_tx: 'ri-money-dollar-circle-line' },
      // Create Demands is hidden from the sidebar — the bulk demand-creation flow is no
      // longer used. The route itself stays registered, so any existing link still works.
      // { mnu_itm_nm: 'Create Demands', mnu_itm_url_tx: '/app/maud-erp/display-devices/create-demands', mnu_itm_icn_tx: 'ri-add-circle-line' },
      { mnu_itm_nm: 'Enforcement', mnu_itm_url_tx: '/app/maud-erp/display-devices/compliance', mnu_itm_icn_tx: 'ri-shield-check-line' },
      { mnu_itm_nm: 'Penalty/Fines', mnu_itm_url_tx: '/app/maud-erp/display-devices/penalties', mnu_itm_icn_tx: 'ri-alarm-warning-line' },
      { mnu_itm_nm: 'Actions', mnu_itm_url_tx: '/app/maud-erp/display-devices/actions', mnu_itm_icn_tx: 'ri-flashlight-line' },
      { mnu_itm_nm: 'Reports', mnu_itm_url_tx: '/app/maud-erp/display-devices/reports', mnu_itm_icn_tx: 'ri-file-chart-line' },
      { mnu_itm_nm: 'Setup', mnu_itm_url_tx: '/app/maud-erp/display-devices/setup', mnu_itm_icn_tx: 'ri-settings-3-line' },
    ]
  },
  {
    mnu_itm_nm: 'App Designer',
    t_mnu_itm_nm: 'యాప్ డిజైనర్',
    mnu_itm_url_tx: '/app/app-designer',
    mnu_itm_icn_tx: 'ri-palette-line',
    mnu_dscn_tx: 'Design workflows, forms, and customize application'
  },
];

const MaudErpLayoutWrapper: React.FC = () => {
  // Module headers run full width, edge to edge with the top bar, so the content
  // wrapper carries no side padding. Asset Management is the exception: its pages
  // were built against the old 8px gutter (they cancel it with -mx-1 sm:-mx-2) and
  // are deliberately left as they are, so the gutter stays on its routes only.
  const { pathname } = useLocation();
  const keepsGutter = pathname.startsWith('/app/maud-erp/asset-management');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useAuth();
  // Collapse state lives in the shared sidebar store (same one components/Layout.tsx
  // uses) instead of local state, so a page can collapse the rail programmatically —
  // e.g. the Add/Edit Employee modal hides it while the full-screen form is open.
  // The store persists itself under 'sidebar-storage'.
  const isSidebarCollapsed = useSidebarStore(state => state.isCollapsed);
  const toggleSidebarCollapse = useSidebarStore(state => state.toggleCollapse);

  const { toasts, removeToast, addToast } = useToast();

  useEffect(() => {
    ThemeService.initializeTheme();
  }, []);

  // One-time migration of the pre-store preference so an existing user's collapsed
  // rail doesn't silently spring open after this switch.
  useEffect(() => {
    const legacy = localStorage.getItem('sidebarCollapsed');
    if (legacy === null) return;
    if (localStorage.getItem('sidebar-storage') === null) {
      try {
        useSidebarStore.getState().setCollapsed(JSON.parse(legacy));
      } catch {
        /* malformed legacy value — fall back to the store default */
      }
    }
    localStorage.removeItem('sidebarCollapsed');
  }, []);

  // Listen for global notifications from non-React code (like authorization service)
  useEffect(() => {
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent<NotificationEvent>;
      if (customEvent.detail) {
        const { type, message, duration } = customEvent.detail;
        addToast({ type, message, duration });
      }
    };

    window.addEventListener(NOTIFICATION_EVENT_NAME, handleNotification as EventListener);

    return () => {
      window.removeEventListener(NOTIFICATION_EVENT_NAME, handleNotification as EventListener);
    };
  }, [addToast]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F3EDE4] dark:bg-[#17110A]">
      {/* Full-width top bar. Pins itself (`sticky top-0` on its own root) and
          publishes its live height as --app-header-h; a page pinning below it
          offsets by that variable. */}
      <Header
        onToggleSidebar={toggleSidebar}
        isSidebarOpen={isSidebarOpen}
        isSidebarCollapsed={isSidebarCollapsed}
        onToggleSidebarCollapse={toggleSidebarCollapse}
      />

      {/* Sidebar card + content, side by side under the bar. Below lg the sidebar
          is a fixed drawer and takes no room in this row. */}
      <div className="flex flex-1 items-start gap-4 p-3 sm:p-4">
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
        menuItems={maudErpMenuItems}
        onToggle={toggleSidebar}
      />

      {/* min-w-0: without it this flex item's automatic minimum width is its content's
          min-width, so any wide child (a many-column table such as the Council meeting
          calendar) stretches the whole shell past the viewport instead of scrolling
          inside its own overflow-x-auto container. Same fix as components/Layout.tsx. */}
      <div className="flex flex-col self-stretch min-h-[calc(100vh-var(--app-header-h,4.25rem)-32px)] flex-1 min-w-0">

        {/*
          No overflow-y here. `main` is flex-1 inside a min-h-screen (not h-screen)
          column, so its height is content-driven and it never scrolls itself —
          the window does. An overflow value other than `visible` would still make
          `main` the nearest scrollport for `position: sticky` descendants, which
          silently breaks them: they stick to a box that scrolls away with the
          page. That is what pinned Finance2's FilterBar off the top of the screen.
        */}
        <main
          className="shell-main flex-1"
          data-theme="main"
        >
          <div className={keepsGutter ? 'px-2 pb-2' : 'pb-2'}>
            <Suspense fallback={<TableSkeleton />}>
              {/* Keyed on the working tenant: switching UDA in the header remounts the
                  page content (so screens that fetch in useEffect([]) reload against the
                  new tenant) while this shell — header, sidebar, footer — stays on screen.
                  Replaces the old window.location.reload() in Header.handleUdaChange. */}
              <Outlet key={user?.tenantId || 'no-tenant'} />
            </Suspense>
          </div>
        </main>

        {/* Footer - Sticky at bottom */}
        <footer className="mt-auto">
          <div className="px-4 py-2">
            <p className="text-center text-xs text-[#7A6A56] dark:text-[#B3A48F]">
              Developed by <span className="font-medium text-[#2A1D0E] dark:text-[#F3EAD9]">Dreamstep Software Innovations Pvt Ltd</span>
            </p>
          </div>
        </footer>
      </div>
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default MaudErpLayoutWrapper;
